import { Upload } from 'lucide-react';
import { OnboardingData } from '../../../types/onboarding';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';

interface ProductInformationManualEntryProps {
  data: OnboardingData;
  handleImageUpload: () => void;
  selectMainImage: (index: number) => void;
  removeImage: (index: number) => void;
  selectedImageIndex: number;
  productData: any;
  updateProductData: (data: any) => void;
  MAX_IMAGES: number;
  uploadProgress: {
    current: number;
    total: number;
    isUploading: boolean;
  };
}

function ProductInformationManualEntry({
  data,
  handleImageUpload,
  selectMainImage,
  removeImage,
  selectedImageIndex,
  productData,
  updateProductData,
  MAX_IMAGES,
  uploadProgress,
}: ProductInformationManualEntryProps) {
  const { language } = useAuth();

  return (
    <div className="border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="mb-4">
        <h4 className="text-lg font-medium">{dictionary["product-information"][language]}</h4>
        <p className="text-sm font-medium text-gray-700 mt-1 mb-3">{dictionary["upload-images-description-onboarding-product-manual"][language]}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          {data.visualContent.images.length > 0 ? (
            <>
              <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 relative">
                <img
                  src={data.visualContent.images[selectedImageIndex]}
                  alt="Main product"
                  className="w-full h-full object-contain"
                />
                {uploadProgress.isUploading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg p-4 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-700">
                        {dictionary["uploading-image"][language] || "uploading image"}: {uploadProgress.current} de {uploadProgress.total}...
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h5 className="text-sm font-medium text-gray-700">
                    {dictionary["product-images"][language] || "Imágenes del producto"} ({data.visualContent.images.length}/{MAX_IMAGES})
                  </h5>
                  {data.visualContent.images.length < MAX_IMAGES ? (
                    <button
                      type="button"
                      onClick={handleImageUpload}
                      disabled={uploadProgress.isUploading}
                      className={`btn btn-secondary text-xs px-3 py-1 ${uploadProgress.isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {uploadProgress.isUploading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-1" />
                          {dictionary["add-more-images"][language] || "Agregar más"}
                        </>
                      )}
                    </button>
                  ) : (
                    <span className="text-xs text-gray-500">
                      {dictionary["max-images-reached"][language] || "Límite alcanzado"}
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-4 gap-2">
                  {data.visualContent.images.map((image, index) => (
                    <div
                      key={index}
                      className="relative group"
                    >
                      <div
                        className={`aspect-square rounded-lg overflow-hidden border cursor-pointer transition-all duration-200 ${
                          index === selectedImageIndex 
                            ? 'border-primary-600 ring-2 ring-primary-600 shadow-md' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => selectMainImage(index)}
                      >
                        <img
                          src={image}
                          alt={`Product view ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                        onClick={() => removeImage(index)}
                        title={dictionary["remove-image"][language] || "Eliminar imagen"}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="border-2 border-dashed border-primary-200 rounded-lg bg-primary-50 p-8 flex flex-col items-center justify-center">
              {uploadProgress.isUploading ? (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    {dictionary["uploading-images"][language] || "uploading images..."}
                  </p>
                  <p className="text-sm text-gray-600">
                    {dictionary["progress"][language] || "progress"}: {uploadProgress.current} de {uploadProgress.total}
                  </p>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleImageUpload}
                    className="btn btn-primary mb-4"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    {dictionary["upload-images"][language]}
                  </button>

                  <p className="text-center text-gray-600">
                    {dictionary["upload-images-description-upload-onboarding-product-manual"][language]}
                  </p>
                  <p className="text-center text-gray-500 text-sm mt-1">
                    {dictionary["accepted-formats"][language]}
                  </p>
                  <p className="text-center text-gray-500 text-sm mt-1">  
                    {dictionary["max-file-size-per-image"][language]}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="product-name" className="block text-sm font-medium text-gray-700 mb-1">
              {dictionary["product-name"][language]}
            </label>
            <input
              id="product-name"
              type="text"
              className="input"
              placeholder={dictionary["product-name-placeholder-manual-entry"][language]}
              value={productData.name}
              onChange={(e) => updateProductData({ ...productData, name: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="product-details" className="block text-sm font-medium text-gray-700 mb-1">
              {dictionary["product-details"][language]}
            </label>
            <textarea
              id="product-details"
              className="textarea h-32"
              placeholder={dictionary["product-details-placeholder-manual-entry"][language]}
              value={productData.details}
              onChange={(e) => updateProductData({ ...productData, details: e.target.value })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductInformationManualEntry;
